import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  Button,
  EmailCodeSignInForm,
  Field,
  SignInLayout,
  SignInNotice,
  SIGN_IN_POOL,
  TextInput,
} from '@bcecloud/portal-ui';
import { poolSwapNeededFor, switchAuthPool, takePendingEmail } from '@/lib/auth-pool';
import { useClerkPool } from '@/components/MyEventsAuthProvider';

/** The hero, in BCE's palette. Tokens, so it tracks the brand. */
const BRAND_ROYAL = 'var(--color-bce-royal)';
const BRAND_NAVY = 'var(--color-bce-navy)';

/**
 * The myevents front door.
 *
 * The same split-screen screen as BCEcloud — one component in portal-ui — and
 * the same passwordless email code. What is specific here is that it serves
 * two audiences: a customer signing in to see their events, and a BCE staff
 * member signing in to see what that customer sees.
 *
 * Which Clerk instance a person goes to is inferred from their address rather
 * than chosen from a link. The email step below renders outside Clerk on
 * purpose: the instance cannot be picked until the address is known, and only
 * one `ClerkProvider` can be mounted at a time.
 *
 * Email codes are now the only way in. The password door that carried the
 * migration has been removed, so a Clerk outage is a full outage here — the
 * legacy bearer token is still accepted by the API, which lets sessions
 * already established drain naturally rather than being cut off.
 *
 * @returns The sign-in screen.
 */
export function LoginPage(): React.ReactElement {
  // From the provider, never re-derived: a component that renders a Clerk hook
  // without a provider above it takes the whole route down.
  const { mounted: clerkConfigured, pool } = useClerkPool();

  // An address parked across the reload that swaps instances, so nobody types
  // their email twice.
  const [resumedEmail, setResumedEmail] = useState<string | null>(null);
  useEffect(() => setResumedEmail(takePendingEmail()), []);

  const staffDoor = pool === SIGN_IN_POOL.staff;

  return (
    <SignInLayout
      masthead={
        <span className="text-[27px] leading-none font-bold tracking-[-2px] text-brand">
          My Events
        </span>
      }
      mastheadLabel="BEST CORPORATE EVENTS"
      eyebrow={staffDoor ? 'BCE STAFF' : 'YOUR EVENTS'}
      headline={staffDoor ? 'See it as your\nclient sees it.' : 'Everything for\nyour event.'}
      subcopy={
        staffDoor
          ? 'Sign in with your BCE account to open My Events the way a customer experiences it.'
          : 'Schedules, headcounts, logistics and documents for every event you have with Best Corporate Events — in one place.'
      }
      notice={
        // One notice for everyone. The staff-door variant was written for a
        // staff member signing in here by hand, which is not how they arrive:
        // their BCEcloud session already carries them in, so the only people
        // reading this are customers.
        <SignInNotice>
          <span aria-hidden="true">✓</span> <b>No password needed.</b> We&rsquo;ll email you a
          one-time code. New to My Events? Signing in with your work address is all it takes.
        </SignInNotice>
      }
      footerLeft="Powered by Best Corporate Events"
      footerRight="My Events"
      hero={{
        fromColor: BRAND_ROYAL,
        toColor: BRAND_NAVY,
        quote: 'The best events feel effortless to everyone but the people running them.',
        statValue: '250+',
        statLabel: 'programs available across North America',
      }}
    >
      {clerkConfigured ? (
        <PooledSignIn resumedEmail={resumedEmail} />
      ) : (
        <p
          role="alert"
          className="rounded-portal-button border border-amber-200 bg-amber-50 p-3 text-portal-tiny text-amber-900"
        >
          <b>Sign-in is not configured in this environment.</b> Set the customer Clerk instance on
          the API, or contact your BCE account team.
        </p>
      )}
    </SignInLayout>
  );
}

/**
 * The email step, plus the code step once the right instance is mounted.
 *
 * The address is collected here rather than inside {@link EmailCodeSignInForm}
 * because it decides which Clerk instance is needed, and that decision has to
 * be made before Clerk is asked anything. When the address belongs to the pool
 * already mounted, the shared form takes over unchanged.
 *
 * @param props - An address resumed across an instance swap, if any.
 * @returns The sign-in control.
 */
function PooledSignIn({
  resumedEmail,
}: {
  readonly resumedEmail: string | null;
}): React.ReactElement {
  const { isLoaded, isSignedIn } = useAuth();
  const [email, setEmail] = useState('');
  const [confirmed, setConfirmed] = useState<string | null>(null);

  // An address carried across the reload is already known to belong to the
  // pool now mounted, so it goes straight to the code step.
  useEffect(() => {
    if (resumedEmail) setConfirmed(resumedEmail);
  }, [resumedEmail]);

  // A session can already exist before anyone touches this form. BCE staff
  // signed into BCEcloud arrive with one, because the team instance's primary
  // domain is bcecloud.com and its cookie is therefore valid on this
  // subdomain — that shared cookie *is* the single-sign-on. Showing the form
  // anyway means the first submit calls signIn.create() on a live session,
  // which Clerk refuses with "You're already signed in".
  //
  // Only once Clerk has actually restored: before that it reports neither
  // signed in nor signed out, and treating "not yet known" as "signed out"
  // would show the form to someone who is already in.
  //
  // Deliberately NOT blocking the render while it loads. Collecting an address
  // needs no Clerk at all, and the submit button is already disabled until
  // isLoaded — whereas gating the whole step means a Clerk that never
  // initialises leaves a blank sign-in page with nothing to act on.
  if (isLoaded && isSignedIn) return <Navigate to="/" replace />;

  if (confirmed !== null) {
    return (
      <EmailCodeSignInForm
        emailLabel="Email"
        emailPlaceholder={confirmed}
        // Customers have always been able to create their own account; the API
        // creates the BCE record on first sign-in.
        allowSignUp
        initialEmail={confirmed}
        autoStart
      />
    );
  }

  /** Routes the address to its pool, swapping instances if it belongs elsewhere. */
  function submit(event: React.FormEvent): void {
    event.preventDefault();
    const address = email.trim();
    if (address.length === 0) return;

    const swapTo = poolSwapNeededFor(address);
    if (swapTo !== null) {
      switchAuthPool(swapTo, address);
      return;
    }
    setConfirmed(address);
  }

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <Field label="Email" htmlFor="email">
        <TextInput
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          placeholder="you@company.com"
        />
      </Field>
      <Button type="submit" variant="primary" className="h-[54px] w-full">
        Continue
      </Button>
    </form>
  );
}
